import { useLocalizationContext } from '@im/react-localization';
import { useDecodedToken, useOktaContext } from 'mf_core/auth';
import React, { ReactNode, useMemo } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { SingleAdBanner } from './internal/SingleAdBanner';
import {
  AdServeRequestTO,
  useAdserveContent,
} from './internal/use-adserveContent';
import Skeleton from '@mui/material/Skeleton';
import { useIsResolution } from '@im/ui-lib/responsive';

type Props = {
  IsCarousel?: boolean;
  /**Use `true` in zenith themed components */
  IsFrameless?: boolean;
  IsMultiAd?: boolean;
  IsPersonalizedAd?: boolean;
  ResourceSize?: number;
  dataKeys?: string[];
  /** @deprecated Will be removed soon */
  enableCache?: boolean;
  /** A fallback component in case current `pageZone` has no ads */
  fallback?: ReactNode | null;
  /** If set, the skeleton loader will appear with specified height. By default loader will be disabled. */
  loaderHeight?: string | number;
  navigable?: boolean;
  pageZone: string;
  pid?: number;
  /**
   * @deprecated Will be removed soon
   */
  refreshInterval?: number;
  serverRefresh?: boolean;
  showPriorityAds?: boolean;
};

export default function AdServeBanner(props: Readonly<Props>) {
  const {
    IsCarousel = false,
    IsFrameless = false,
    IsMultiAd = false,
    IsPersonalizedAd = false,
    ResourceSize,
    dataKeys = [],
    fallback = null,
    loaderHeight,
    navigable = true,
    pageZone,
    pid,
    serverRefresh,
    showPriorityAds = false,
  } = props;
  const { locale } = useLocalizationContext();
  const decodedToken = useDecodedToken();
  const oktaContext = useOktaContext();
  const isAuthenticated = oktaContext.authState?.isAuthenticated ?? false;

  const isMobile = useIsResolution({ max: 768 });
  const params: AdServeRequestTO = useMemo(() => {
    const country = `country:${locale?.split('-')?.pop() ?? ''}`;
    const culture = `culture:${locale?.split('-')?.shift() ?? ''}`;
    const logout = `logout:${!isAuthenticated}`;
    let segments: string[] = [];
    segments =
      decodedToken?.segments?.userAdbutler?.map((segment) => {
        return `segment:${segment}`;
      }) ?? [];

    const request: AdServeRequestTO = {
      AdButlerDataKey: [
        `sitecode:${locale}`,
        country,
        culture,
        logout,
        ...dataKeys,
        ...segments,
      ],
      EmailId: decodedToken?.imoProfile?.emailAddress ?? '',
      IsCarousel: IsCarousel,
      IsMultiAd: IsMultiAd,
      IsPersonalizedAd: isAuthenticated && IsPersonalizedAd,
      ResellerId: decodedToken?.resellerId ?? '',
      UserName: decodedToken?.imoProfile?.userName ?? '',
      Zone: pageZone,
    };
    if (IsCarousel) {
      request.ResourceSize = ResourceSize;
    }
    if (pid) {
      request.pid = pid;
    }

    return request;
  }, [
    locale,
    isAuthenticated,
    decodedToken?.segments?.userAdbutler,
    decodedToken?.imoProfile?.emailAddress,
    decodedToken?.imoProfile?.userName,
    decodedToken?.resellerId,
    dataKeys,
    IsCarousel,
    IsMultiAd,
    IsPersonalizedAd,
    pageZone,
    pid,
    ResourceSize,
  ]);
  const { data: adServeData, isLoading } = useAdserveContent(params);

  if (isLoading) {
    return (
      <>
        {!!loaderHeight && (
          <Skeleton height={loaderHeight} variant="rectangular" width="100%" />
        )}
      </>
    );
  }

  if (adServeData && adServeData.status !== 'NO_ADS') {
    return (
      <SingleAdBanner
        IsCarousel={IsCarousel}
        IsFrameless={IsFrameless || isMobile}
        IsMultiAd={IsMultiAd}
        IsPersonalizedAd={IsPersonalizedAd}
        data={adServeData}
        navigable={navigable}
        noRefresh={pageZone === 'HomePageVideoZone'}
        pageZone={pageZone}
        serverRefresh={serverRefresh}
        showPriorityAds={showPriorityAds}
        type={pageZone === 'HomePageVideoZone' ? 'Adbutler Video' : undefined}
      />
    );
  } else return <>{fallback}</>;
}
